import { useState, useEffect, useRef } from 'react';

export default function Process() {
  const [visibleSteps, setVisibleSteps] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const steps = entry.target.querySelectorAll('[data-step-index]');
            steps.forEach((step, index) => {
              setTimeout(() => {
                setVisibleSteps((prev) => [...prev, index]);
              }, index * 200);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      icon: 'ri-smartphone-line',
      title: 'Objednáte online',
      description: 'Vyplníte formulář a odhadnete počet kusů oblečení'
    },
    {
      icon: 'ri-truck-line',
      title: 'Svezeme prádlo',
      description: 'Přijedeme k vám domů a spočítáme skutečný počet kusů'
    },
    {
      icon: 'ri-t-shirt-line',
      title: 'Vyžehlíme',
      description: 'Profesionálně vyžehlíme každý kus oblečení'
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Doručíme zpět',
      description: 'Přivezeme čisté a vyžehlené prádlo až k vám domů'
    }
  ];

  return (
    <section id="jak-to-funguje" ref={sectionRef} className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Jednoduše. Ve třech krocích.
          </h2>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Jednoduchý proces ve čtyřech krocích
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connection Lines */}
          <div className="hidden lg:block absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-200 via-teal-300 to-teal-200 dark:from-teal-900/30 dark:via-teal-800/30 dark:to-teal-900/30"></div>

          {steps.map((step, index) => (
            <div
              key={index}
              data-step-index={index}
              className={`relative transition-all duration-700 ${
                visibleSteps.includes(index)
                  ? 'opacity-100 translate-y-0 scale-100'
                  : 'opacity-0 translate-y-10 scale-95'
              }`}
              style={{
                transitionDelay: visibleSteps.includes(index) ? '0ms' : `${index * 200}ms`,
              }}
            >
              <div className="text-center">
                <div className="relative inline-block mb-4 sm:mb-6">
                  <div
                    className={`w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center rounded-full mx-auto ${
                      index % 2 === 0
                        ? 'bg-teal-600 dark:bg-teal-700'
                        : 'bg-teal-500 dark:bg-teal-600'
                    } shadow-lg transform transition-transform duration-500 ${
                      visibleSteps.includes(index) ? 'rotate-0' : 'rotate-180'
                    }`}
                  >
                    <i className={`${step.icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>
                  <div
                    className="absolute -top-2 -right-2 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-teal-700 dark:bg-teal-800 text-white font-bold text-sm shadow-md"
                  >
                    {index + 1}
                  </div>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
